import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import classnames from 'classnames'
import _ from 'lodash'

import Card from '@material-ui/core/Card'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import CardHeader from '@material-ui/core/CardHeader'
import CardMedia from '@material-ui/core/CardMedia'
import Collapse from '@material-ui/core/Collapse'
import Dialog from '@material-ui/core/Dialog'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'

import { COUNTRIES, CRS, FUZZY, GEO_ACCURACIES, LOCATION_TYPES, MULTIMATCH, POSTCODE_MATCH } from '../../constants'

const styles = theme => ({
  paper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textAlign: 'center',
    padding: theme.spacing.unit
  },
  header: {
    margin: theme.spacing.unit*2
  },
  subheading: {
    paddingTop: theme.spacing.unit*2,
    margin: theme.spacing.unit*2
  },
  card: {
    margin: theme.spacing.unit
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    textAlign: 'center',
    margin: 'auto'
  },
  textField: {
    textAlign: 'left',
    margin: theme.spacing.unit*2,
    width: 230,
  },
  menu: {
    textAlign: 'left',
    margin: theme.spacing.unit*2,
    width: 230,
  },
  actions: {
    display: 'flex',
    margin: 'auto'
  },
  expand: {
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    marginLeft: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginRight: -8,
    },
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  map: {
    height: 250,
    width: 600
  },
  properties: {
    height: 250,
    overflow: 'scroll'
  }
})

// Convert address into heading
const getAddressHeadline = (address) =>
  _.values(_.pick(_.get(address, 'properties', {}),
    ['organisation', 'sub_building', 'building_name', 'building_group',
    'building_number'])).join(', ')  + ' ' +
  _.values(_.pick(_.get(address, 'properties', {}),
    ['secondary_street', 'primary_street'])).join(', ')

// Convert address into subheading
const getAddressSubheading = (address) =>
  _.values(_.pick(_.get(address, 'properties', {}), ['sub_locality',
    'locality', 'subregion', 'region'])).join(', ') +
  (_.get(address, 'properties.postcode') ? ` ${address.properties.postcode}` : '') +
  (_.get(address, 'properties.uprn') ? ` (UPRN: ${address.properties.uprn})` : '')

const getAddressList = (address) => {
  const { properties, geometry } = address
  return (
    <ul>
      { Object.keys(properties).map(k => <li key={k}><b>{k}</b>: {properties[k].toString()}</li>) }
      <li><b>lat</b>: {_.get(geometry, 'coordinates.lat')}</li>
      <li><b>lon</b>: {_.get(geometry, 'coordinates.lon')}</li>
      <li><b>geo_accuracy</b>: {_.get(geometry, 'geo_accuracy')}</li>
    </ul>
  )
}

class Search extends React.Component {

  constructor() {
    super()
    this.state = {
      expanded: false,
      selected: '',
      open: false,
      error: null
    }
  }

  handleExpandClick = () => {
    this.setState(state => ({ expanded: !state.expanded }))
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
    })
  }

  handleSubmit = e => {
    e.preventDefault()
    const { handleSearch, prefs } = this.props
    const environment = prefs.environments[this.state.environment]
    const query = this.state.query
    if (!query) {
      alert('Query is required')
    } else {
      const opts = _.pick(this.state, 'country', 'multimatch', 'min_match',
        'min_accuracy', 'postcode_match', 'max_results', 'crs', 'fuzzy',
        'delivery_point_only', 'show_alts')
      handleSearch(environment, 'address', query, opts)
    }
  }

  handleClickRow = (event, locationType, id) => {
    const { handleLookup, prefs } = this.props
    const environment = prefs.environments[this.state.environment]
    this.setState({ selected: id })
    handleLookup(environment, locationType, id)
    this.setState({ open: true })
  }

  handleReset = () => {
    const { prefs } = this.props
    this.setState({ ...prefs.defaults, query: '', address: null, result: null })
  }

  handleClickOpen = () => {
    this.setState({ open: true })
  }

  handleClose = value => {
    this.setState({ open: false })
  }

  componentWillMount() {
    this.handleReset()
  }

  render() {
    const { classes, prefs, address, result } = this.props
    return (
      <Paper className={classes.paper} elevation={1}>
        <Typography gutterBottom variant='headline' color='secondary' className={classes.header}>
          Search
        </Typography>
        <Card className={classes.card}>
          <form className={classes.container} noValidate autoComplete='off' onSubmit={this.handleSubmit}>
            <Grid container justify='center' spacing={16} className={classes.container}>
              <Grid item sm={6} md={3}>
                <TextField
                  required
                  id='query'
                  label='Query'
                  className={classes.textField}
                  value={this.state.query}
                  onChange={this.handleChange('query')}
                  margin='normal'
                />
              </Grid>
              <Grid item sm={6} md={3}>
                <TextField
                  id='select-country'
                  select
                  label='Country'
                  className={classes.textField}
                  value={this.state.country}
                  onChange={this.handleChange('country')}
                  SelectProps={{ MenuProps: { className: classes.menu } }}
                  margin='normal'
                >
                  { COUNTRIES.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item sm={6} md={3}>
                <TextField
                  id='select-multimatch'
                  select
                  label='Multimatch'
                  className={classes.textField}
                  value={this.state.multimatch}
                  onChange={this.handleChange('multimatch')}
                  SelectProps={{ MenuProps: { className: classes.menu } }}
                  margin='normal'
                >
                  { MULTIMATCH.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                </TextField>
              </Grid>
              <Grid item sm={6} md={3}>
                <TextField
                  id='select-environment'
                  select
                  label='Environment'
                  className={classes.textField}
                  value={this.state.environment}
                  onChange={this.handleChange('environment')}
                  SelectProps={{ MenuProps: { className: classes.menu } }}
                  margin='normal'
                >
                  { Object.keys(prefs.environments).map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                </TextField>
              </Grid>
            </Grid>
            {/* Action Buttons */}
            <CardActions className={classes.actions} disableActionSpacing>
              <IconButton color='secondary' aria-label='Reset' onClick={this.handleReset}>
                <ClearIcon />
              </IconButton>
              <IconButton
                color='secondary'
                className={classnames(classes.expand, {
                  [classes.expandOpen]: this.state.expanded,
                })}
                onClick={this.handleExpandClick}
                aria-expanded={this.state.expanded}
                aria-label='Show more'
              >
                <ExpandMoreIcon />
              </IconButton>
              <IconButton type='submit' color='secondary' aria-label='Search'>
                <SearchIcon />
              </IconButton>
            </CardActions>

            {/*  Advanced Search Options */}
            <Collapse in={this.state.expanded} timeout='auto' unmountOnExit>
              <Grid container justify='center' spacing={16} className={classes.container}>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-min_match'
                    select
                    label='Minimum Match'
                    className={classes.textField}
                    value={this.state.min_match}
                    onChange={this.handleChange('min_match')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { LOCATION_TYPES.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-min_accuracy'
                    select
                    label='Minimum Accuracy'
                    className={classes.textField}
                    value={this.state.min_accuracy}
                    onChange={this.handleChange('min_accuracy')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { GEO_ACCURACIES.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-postcode_match'
                    select
                    label='Postcode Match'
                    className={classes.textField}
                    value={this.state.postcode_match}
                    onChange={this.handleChange('postcode_match')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { POSTCODE_MATCH.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-max_results'
                    select
                    label='Max Results'
                    className={classes.textField}
                    value={this.state.max_results}
                    onChange={this.handleChange('max_results')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { [ 10, 50, 100, 250 ].map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-crs'
                    select
                    label='Coordinate System'
                    className={classes.textField}
                    value={this.state.crs}
                    onChange={this.handleChange('crs')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { CRS.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-fuzzy'
                    select
                    label='Fuzzy'
                    className={classes.textField}
                    value={this.state.fuzzy}
                    onChange={this.handleChange('fuzzy')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { FUZZY.map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-delivery_point_only'
                    select
                    label='Only Show Delivery Points'
                    className={classes.textField}
                    value={this.state.delivery_point_only}
                    onChange={this.handleChange('delivery_point_only')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { [ 'true', 'false' ].map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
                <Grid item sm={6} md={3}>
                  <TextField
                    id='select-show_alts'
                    select
                    label='Show Alternatives'
                    className={classes.textField}
                    value={this.state.show_alts}
                    onChange={this.handleChange('show_alts')}
                    SelectProps={{ MenuProps: { className: classes.menu } }}
                    margin='normal'
                  >
                    { [ 'true', 'false' ].map(v => (<MenuItem key={v} value={v}>{v}</MenuItem>))}
                  </TextField>
                </Grid>
              </Grid>
            </Collapse>
          </form>
        </Card>

        {/* Match Status */}
        { result.match_status &&
          <Typography gutterBottom variant='subheading' align='left' color='secondary' className={classes.subheading}>
            Result: {result.match_status} ( {result.found} {result.found === 1 ? 'match' : 'matches'} found )
          </Typography>
        }

        {/* Results Table */}
        { result.results &&
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell numeric>Score</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.results.map(n => {
                return (
                  <TableRow key={n.id} hover selected={n.id === this.state.selected}
                    onClick={event => this.handleClickRow(event, n.location_type, n.id)}>
                    <TableCell component="th" scope="row">
                      {n.location_type}
                    </TableCell>
                    <TableCell>{n.description}</TableCell>
                    <TableCell numeric>{n.score}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        }

        {/* Address Dialog */}
        { address.result &&
          <Dialog open={this.state.open} onClose={this.handleClose}>
            <Card>
              <CardHeader
                title={getAddressHeadline(address.result)}
                subheader={getAddressSubheading(address.result)}
              />
              <CardMedia
                className={classes.map}
                component='img'
                src={`https://api.mapbox.com/styles/v1/addresscloud/cjki63m0v39qt2rteke3a2kn7/static/pin-s-circle+285A98(${address.result.geometry.coordinates.lon},${address.result.geometry.coordinates.lat})/${address.result.geometry.coordinates.lon},${address.result.geometry.coordinates.lat},16,0/600x250@2x?access_token=pk.eyJ1IjoiYWRkcmVzc2Nsb3VkIiwiYSI6ImNqODM2MzZrYTMxajEyd280YXFseG4xeGEifQ.xQ9RxXGZ0Tdxh5YB_0-V5w`}
                title='Contains Ordnance Survey data © Crown copyright and database right 2018'
              />
              <CardContent>{ getAddressList(address.result) }</CardContent>
            </Card>
          </Dialog>
        }

      </Paper>

    )
  }
}

Search.propTypes = {
  classes: PropTypes.object.isRequired,
  prefs: PropTypes.object.isRequired,
  address: PropTypes.object.isRequired,
  result: PropTypes.object.isRequired,
  handleLookup: PropTypes.func.isRequired,
  handleSearch: PropTypes.func.isRequired
}

export default withStyles(styles)(Search)
